import { IAssessment } from "@/modules/assessments/assessments.types";
import { ITextLocation } from "@/modules/assessments/scenes/CreateAssessment/assessment.interface";
import { TextLocations } from "@/modules/assessments/scenes/CreateAssessment/createAssessment.constants";
import { resolveEffectiveLocales } from "@/shared/utils/locale.utils";

interface IInitialValues {
  name: Record<string, string>;
  clientJobTitle: Record<string, string>;
  description: string;
  photo?: string;
  landingPhoto?: string;
  enableStartReminder: boolean;
  startReminderDays: number;
  enableFinishReminder: boolean;
  finishReminderDays: number;
  showScaleDevelopment?: boolean;
  showInterviewGuide?: boolean;
  feedbackEnabled: boolean;
  openEndedEnabled: boolean;
  demographicsEnabled: boolean;
  employeeIdEnabled: boolean;
  resultValidityDays: number;
  enabledLocales: string[];
  [TextLocations.introText]: Record<string, string>;
  [TextLocations.landingText]: Record<string, string>;
  [TextLocations.outroText]: Record<string, string>;
}

export function getInitialAssessmentValues(textLocations: ITextLocation[], assessment?: IAssessment) {
  const introTextId = textLocations.find((textLocation) => textLocation.name === TextLocations.introText)?.locationId;
  const landingTextId = textLocations.find(
    (textLocation) => textLocation.name === TextLocations.landingText,
  )?.locationId;
  const outroTextId = textLocations.find((textLocation) => textLocation.name === TextLocations.outroText)?.locationId;

  let initialValues: IInitialValues = {
    name: {},
    clientJobTitle: {},
    description: "",
    photo: undefined,
    landingPhoto: undefined,
    enableStartReminder: false,
    startReminderDays: 3,
    enableFinishReminder: false,
    finishReminderDays: 3,
    feedbackEnabled: true,
    openEndedEnabled: true,
    demographicsEnabled: false,
    employeeIdEnabled: false,
    resultValidityDays: 365,
    enabledLocales: resolveEffectiveLocales(assessment?.enabledLocales),
    [TextLocations.introText]: {},
    [TextLocations.landingText]: {},
    [TextLocations.outroText]: {},
  };

  if (assessment?.name) {
    initialValues.name = assessment.name;
  }
  if (assessment?.description) {
    initialValues.description = assessment.description;
  }

  if (assessment?.assessmentTexts) {
    if (assessment.assessmentTexts?.length !== 0) {
      initialValues[TextLocations.introText] =
        assessment.assessmentTexts.find((text) => text.locationId === introTextId)?.body ?? {};
      initialValues[TextLocations.landingText] =
        assessment.assessmentTexts.find((text) => text.locationId === landingTextId)?.body ?? {};
      initialValues[TextLocations.outroText] =
        assessment.assessmentTexts.find((text) => text.locationId === outroTextId)?.body ?? {};
    }
  }

  if (assessment?.clientJobTitle) {
    initialValues.clientJobTitle = assessment.clientJobTitle;
  }

  if (assessment?.startReminderDays) {
    initialValues.enableStartReminder = true;
    initialValues.startReminderDays = assessment.startReminderDays;
  }

  if (assessment?.finishReminderDays) {
    initialValues.enableFinishReminder = true;
    initialValues.finishReminderDays = assessment.finishReminderDays;
  }

  if (assessment?.showScaleDevelopment !== undefined) {
    initialValues.showScaleDevelopment = assessment.showScaleDevelopment;
  }

  if (assessment?.showInterviewGuide !== undefined) {
    initialValues.showInterviewGuide = assessment.showInterviewGuide;
  }

  if (assessment?.titleImageUrl) {
    initialValues.photo = assessment.titleImageUrl;
  }

  if (assessment?.landingImageUrl) {
    initialValues.landingPhoto = assessment.landingImageUrl;
  }

  if (assessment?.demographicsEnabled !== undefined) {
    initialValues.demographicsEnabled = assessment.demographicsEnabled;
  }

  if (assessment?.employeeIdEnabled !== undefined) {
    initialValues.employeeIdEnabled = assessment.employeeIdEnabled;
  }

  if (assessment?.resultValidityDays !== undefined) {
    initialValues.resultValidityDays = assessment.resultValidityDays;
  }

  return initialValues;
}

// Keeps only the given locales, filling missing ones with "" so the payload covers exactly them.
export function pickLocaleBody(body: Record<string, string> | undefined, locales: string[]): Record<string, string> {
  return locales.reduce<Record<string, string>>((acc, locale) => {
    acc[locale] = body?.[locale] ?? "";
    return acc;
  }, {});
}

/**
 * Keeps only the locales that actually carry a title, trimmed. Unlike pickLocaleBody, a blank entry
 * is dropped rather than stored as "" — a stored empty string counts as a real value during
 * resolution, so it would render blank instead of falling back to English.
 *
 * @param {Record<string, string> | undefined} titles - the locale map from the form
 * @param {string[]} locales - the locales being saved
 * @returns {Record<string, string>} the map with blank locales omitted
 */
export function pickLocaleTitles(
  titles: Record<string, string> | undefined,
  locales: string[],
): Record<string, string> {
  return locales.reduce<Record<string, string>>((acc, locale) => {
    const value = (titles?.[locale] ?? "").trim();
    if (value !== "") {
      acc[locale] = value;
    }
    return acc;
  }, {});
}

// Drives the all-or-nothing rule: true once any selected locale has (trimmed) content.
export function hasAnyLocaleContent(body: Record<string, string> | undefined, locales: string[]): boolean {
  return locales.some((locale) => (body?.[locale] ?? "").trim() !== "");
}

/**
 * Collapses an empty locale map to null, which the API treats as "leave this optional title unset".
 * An empty object is not equivalent: it reaches the write path and is rejected.
 *
 * @param {Record<string, string>} titles - the picked locale map
 * @returns {Record<string, string> | null} the map, or null when it carries no locales
 */
export function emptyToNull(titles: Record<string, string>): Record<string, string> | null {
  return Object.keys(titles).length > 0 ? titles : null;
}
